import React, { useState, useEffect } from 'react';

type TaskSource = {
  id: string;
  name: string;
  type: 'human' | 'ai' | 'system' | 'integration' | 'mcp';
  avatar: string;
  color: string;
  example: string;
};

const TaskTapSources = () => {
  const [activeTasks, setActiveTasks] = useState<any[]>([]);
  const [stats, setStats] = useState({
    humanTasks: 0,
    aiTasks: 0,
    systemTasks: 0,
    integrationTasks: 0,
    mcpTasks: 0
  });

  const sources: TaskSource[] = [
    {
      id: 'ceo',
      name: 'Brian (CEO)',
      type: 'human',
      avatar: '👤',
      color: 'blue',
      example: 'Need competitive analysis of 3 competitors by EOD - $75'
    },
    {
      id: 'claude',
      name: 'Claude (AI Assistant)',
      type: 'ai',
      avatar: '🤖',
      color: 'purple',
      example: 'I found 15 broken links in docs. Creating tasks to fix each - $10/link'
    },
    {
      id: 'monitoring',
      name: 'Monitoring System',
      type: 'system',
      avatar: '📊',
      color: 'yellow',
      example: 'ALERT: Server logs show errors. Task: Investigate and fix - $50'
    },
    {
      id: 'github',
      name: 'GitHub Actions',
      type: 'integration',
      avatar: '🐙',
      color: 'green',
      example: 'PR #234 needs review before merge - $20'
    },
    {
      id: 'mcp',
      name: 'MCP Server',
      type: 'mcp',
      avatar: '🔌',
      color: 'orange',
      example: 'Tool request: Create Python script for data processing - $40'
    },
    {
      id: 'customer',
      name: 'Customer Support',
      type: 'human',
      avatar: '💬',
      color: 'pink',
      example: 'Customer requested tutorial video for new feature - $60'
    },
    {
      id: 'calendar',
      name: 'Calendar Bot',
      type: 'system',
      avatar: '📅',
      color: 'indigo',
      example: 'Weekly task: Update metrics dashboard - $25'
    },
    {
      id: 'sales',
      name: 'Sales Team',
      type: 'human',
      avatar: '💼',
      color: 'teal',
      example: 'URGENT: Need pitch deck updates for 3pm meeting - $100'
    }
  ];

  const createTask = (source: TaskSource) => {
    const taskId = `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const newTask = {
      id: taskId,
      source: source.name,
      type: source.type,
      avatar: source.avatar,
      task: source.example,
      status: 'posted',
      timestamp: new Date().toLocaleTimeString()
    };

    setActiveTasks(prev => [newTask, ...prev].slice(0, 10));

    // Update stats
    setStats(prev => ({
      ...prev,
      [`${source.type}Tasks`]: prev[`${source.type}Tasks`] + 1
    }));

    // Simulate task being claimed
    setTimeout(() => {
      setActiveTasks(prev =>
        prev.map(t => t.id === taskId ? { ...t, status: 'claimed' } : t)
      );
    }, 2000 + Math.random() * 3000);

    // Simulate task being completed
    setTimeout(() => {
      setActiveTasks(prev =>
        prev.map(t => t.id === taskId ? { ...t, status: 'completed' } : t)
      );
    }, 5000 + Math.random() * 5000);
  };

  // Auto-generate tasks from different sources
  useEffect(() => {
    const interval = setInterval(() => {
      const randomSource = sources[Math.floor(Math.random() * sources.length)];
      createTask(randomSource);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'posted': return 'text-yellow-400';
      case 'claimed': return 'text-blue-400';
      case 'completed': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusEmoji = (status: string) => {
    switch (status) {
      case 'posted': return '📢';
      case 'claimed': return '📋';
      case 'completed': return '✅';
      default: return '⏳';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-4">TaskTap: Multiple Task Sources</h1>
          <p className="text-xl text-gray-400">Tasks flow from everywhere - humans, AI, systems, and integrations</p>
        </div>

        {/* Task Sources Grid */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4">Who Creates Tasks?</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {sources.map(source => (
              <button
                key={source.id}
                onClick={() => createTask(source)}
                className={`p-4 rounded-lg bg-gray-800 border border-gray-700 hover:border-${source.color}-500 transition-all hover:scale-105`}
              >
                <div className="text-4xl mb-2">{source.avatar}</div>
                <div className="font-semibold">{source.name}</div>
                <div className="text-xs text-gray-500 capitalize">{source.type}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Live Task Feed */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div>
            <h2 className="text-2xl font-bold mb-4">📊 Live Task Feed</h2>
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 h-96 overflow-y-auto">
              {activeTasks.length === 0 ? (
                <p className="text-gray-500 text-center mt-8">Tasks will appear here...</p>
              ) : (
                <div className="space-y-3">
                  {activeTasks.map(task => (
                    <div
                      key={task.id}
                      className="p-3 bg-gray-900 rounded border-l-4 border-blue-500 animate-slideIn"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center">
                          <span className="text-2xl mr-2">{task.avatar}</span>
                          <div>
                            <div className="font-semibold">{task.source}</div>
                            <div className="text-xs text-gray-500">{task.timestamp}</div>
                          </div>
                        </div>
                        <span className={`${getStatusColor(task.status)} text-sm font-semibold`}>
                          {getStatusEmoji(task.status)} {task.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-300">{task.task}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Source Breakdown */}
          <div>
            <h2 className="text-2xl font-bold mb-4">📈 Task Sources Analytics</h2>
            <div className="space-y-4">
              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-blue-400">👤 Human Tasks</span>
                  <span className="text-2xl font-bold">{stats.humanTasks}</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${(stats.humanTasks / 20) * 100}%` }}></div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-purple-400">🤖 AI-Generated Tasks</span>
                  <span className="text-2xl font-bold">{stats.aiTasks}</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div className="bg-purple-500 h-2 rounded-full" style={{ width: `${(stats.aiTasks / 20) * 100}%` }}></div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-yellow-400">📊 System-Triggered Tasks</span>
                  <span className="text-2xl font-bold">{stats.systemTasks}</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div className="bg-yellow-500 h-2 rounded-full" style={{ width: `${(stats.systemTasks / 20) * 100}%` }}></div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-green-400">🔗 Integration Tasks</span>
                  <span className="text-2xl font-bold">{stats.integrationTasks}</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div className="bg-green-500 h-2 rounded-full" style={{ width: `${(stats.integrationTasks / 20) * 100}%` }}></div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-orange-400">🔌 MCP Server Tasks</span>
                  <span className="text-2xl font-bold">{stats.mcpTasks}</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div className="bg-orange-500 h-2 rounded-full" style={{ width: `${(stats.mcpTasks / 20) * 100}%` }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Example Scenarios */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h2 className="text-2xl font-bold mb-4">🎯 Real-World Scenarios</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-purple-400 mb-2">🤖 Claude Finds Issues</h3>
              <p className="text-sm text-gray-300 mb-2">
                Claude reviews codebase, finds 10 functions missing documentation.
              </p>
              <div className="bg-black rounded p-3 text-xs font-mono">
                <span className="text-purple-400">Claude:</span> "I found 10 undocumented functions. Creating tasks:"<br />
                → Task: Document getUserById() - $15<br />
                → Task: Document processPayment() - $20<br />
                → Task: Document validateInput() - $15<br />
                <span className="text-green-400">→ All claimed within 5 minutes</span>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-yellow-400 mb-2">📊 Monitoring Alert</h3>
              <p className="text-sm text-gray-300 mb-2">
                System detects performance degradation, creates urgent task.
              </p>
              <div className="bg-black rounded p-3 text-xs font-mono">
                <span className="text-yellow-400">System:</span> "ALERT: API response time &gt; 2s"<br />
                → Task: Investigate slow API endpoint - $75 URGENT<br />
                → Priority: HIGH<br />
                → Auto-escalates if not claimed in 10 min<br />
                <span className="text-blue-400">→ DevOps engineer claims in 2 minutes</span>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-green-400 mb-2">🐙 GitHub Integration</h3>
              <p className="text-sm text-gray-300 mb-2">
                PR needs review, GitHub Action creates task automatically.
              </p>
              <div className="bg-black rounded p-3 text-xs font-mono">
                <span className="text-green-400">GitHub:</span> "PR #456 ready for review"<br />
                → Task: Review auth changes in PR #456 - $25<br />
                → Files: 5 changed<br />
                → Complexity: Medium<br />
                <span className="text-green-400">→ Senior dev claims and reviews</span>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-orange-400 mb-2">🔌 MCP Server Request</h3>
              <p className="text-sm text-gray-300 mb-2">
                MCP server needs a tool built, posts task for implementation.
              </p>
              <div className="bg-black rounded p-3 text-xs font-mono">
                <span className="text-orange-400">MCP:</span> "Tool needed: CSV to JSON converter"<br />
                → Task: Build CSV parser tool - $50<br />
                → Requirements: Handle large files<br />
                → Language: Python or Node.js<br />
                <span className="text-blue-400">→ Developer claims and delivers</span>
              </div>
            </div>
          </div>
        </div>

        {/* The Power */}
        <div className="mt-8 text-center p-6 bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-lg border border-blue-500">
          <h2 className="text-3xl font-bold mb-4">The Power of Multiple Sources</h2>
          <p className="text-xl text-gray-300 mb-4">
            Tasks flow from everywhere - humans, AI, systems, and integrations
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div>
              <div className="text-3xl font-bold text-blue-400">24/7</div>
              <div className="text-sm text-gray-400">Task Creation</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-400">&lt; 2min</div>
              <div className="text-sm text-gray-400">Avg Claim Time</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-yellow-400">∞</div>
              <div className="text-sm text-gray-400">Scale Potential</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-400">100%</div>
              <div className="text-sm text-gray-400">Automated</div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .animate-slideIn {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default TaskTapSources;